export interface StoreThemeColors {
  primary: string;
  secondary: string;
  accent: string;
}

export const DEFAULT_STORE_THEME_COLORS: StoreThemeColors = {
  primary: '#E6007E',
  secondary: '#D4AF37',
  accent: '#F7F7F7',
};

const DEFAULT_THEME_TEXT = '#231F20';
const DEFAULT_THEME_TEXT_SOFT = '#6E6067';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const expandHex = (value: string) => {
  const trimmed = value.trim();
  if (!/^#?[0-9a-fA-F]{3,6}$/.test(trimmed)) return null;

  const normalized = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
  if (normalized.length === 3) {
    return `#${normalized
      .split('')
      .map((char) => `${char}${char}`)
      .join('')
      .toUpperCase()}`;
  }

  if (normalized.length === 6) {
    return `#${normalized.toUpperCase()}`;
  }

  return null;
};

export const isValidHexColor = (value: string) => !!expandHex(value);

export const normalizeHexColor = (value: string, fallback: string) => expandHex(value) || fallback;

const hexToRgbTuple = (hex: string): [number, number, number] => {
  const normalized = normalizeHexColor(hex, '#000000').slice(1);
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return [r, g, b];
};

const rgbToHex = (r: number, g: number, b: number) =>
  `#${[r, g, b]
    .map((channel) => clamp(Math.round(channel), 0, 255).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;

const toRgbChannels = (hex: string) => hexToRgbTuple(hex).join(' ');

const mixHex = (base: string, mixWith: string, mixWeight: number) => {
  const ratio = clamp(mixWeight, 0, 1);
  const [r1, g1, b1] = hexToRgbTuple(base);
  const [r2, g2, b2] = hexToRgbTuple(mixWith);
  return rgbToHex(
    r1 * (1 - ratio) + r2 * ratio,
    g1 * (1 - ratio) + g2 * ratio,
    b1 * (1 - ratio) + b2 * ratio
  );
};

const getContrastText = (hex: string) => {
  const [r, g, b] = hexToRgbTuple(hex).map((channel) => channel / 255);
  const luminance =
    [r, g, b]
      .map((channel) =>
        channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4)
      )
      .reduce((acc, channel, index) => acc + channel * [0.2126, 0.7152, 0.0722][index], 0);

  return luminance > 0.58 ? '#231F20' : '#FFFFFF';
};

export const buildStoreThemeTokens = (input?: Partial<StoreThemeColors>) => {
  const primary = normalizeHexColor(input?.primary || '', DEFAULT_STORE_THEME_COLORS.primary);
  const secondary = normalizeHexColor(input?.secondary || '', DEFAULT_STORE_THEME_COLORS.secondary);
  const accent = normalizeHexColor(input?.accent || '', DEFAULT_STORE_THEME_COLORS.accent);

  const primarySoft = mixHex(primary, '#FFFFFF', 0.9);
  const primaryStrong = mixHex(primary, '#000000', 0.12);
  const secondarySoft = mixHex(secondary, '#FFFFFF', 0.84);
  const secondaryStrong = mixHex(secondary, '#000000', 0.16);
  const surfaceMuted = mixHex(accent, primary, 0.05);
  const border = mixHex(accent, primary, 0.14);
  const onPrimary = getContrastText(primary);
  const onSecondary = getContrastText(secondary);

  return {
    primary,
    secondary,
    accent,
    surface: accent,
    primarySoft,
    primaryStrong,
    secondarySoft,
    secondaryStrong,
    surfaceMuted,
    border,
    text: DEFAULT_THEME_TEXT,
    textSoft: DEFAULT_THEME_TEXT_SOFT,
    onPrimary,
    onSecondary,
  };
};

export const applyStoreTheme = (
  input?: Partial<StoreThemeColors>,
  root: HTMLElement = document.documentElement
) => {
  const tokens = buildStoreThemeTokens(input);

  root.style.setProperty('--color-brand-primary', tokens.primary);
  root.style.setProperty('--color-brand-primary-rgb', toRgbChannels(tokens.primary));
  root.style.setProperty('--color-brand-primary-soft', tokens.primarySoft);
  root.style.setProperty('--color-brand-primary-soft-rgb', toRgbChannels(tokens.primarySoft));
  root.style.setProperty('--color-brand-primary-strong', tokens.primaryStrong);
  root.style.setProperty('--color-brand-primary-strong-rgb', toRgbChannels(tokens.primaryStrong));

  root.style.setProperty('--color-brand-secondary', tokens.secondary);
  root.style.setProperty('--color-brand-secondary-rgb', toRgbChannels(tokens.secondary));
  root.style.setProperty('--color-brand-secondary-soft', tokens.secondarySoft);
  root.style.setProperty('--color-brand-secondary-soft-rgb', toRgbChannels(tokens.secondarySoft));
  root.style.setProperty('--color-brand-secondary-strong', tokens.secondaryStrong);
  root.style.setProperty(
    '--color-brand-secondary-strong-rgb',
    toRgbChannels(tokens.secondaryStrong)
  );

  root.style.setProperty('--color-brand-surface', tokens.accent);
  root.style.setProperty('--color-brand-surface-rgb', toRgbChannels(tokens.accent));
  root.style.setProperty('--color-brand-surface-muted', tokens.surfaceMuted);
  root.style.setProperty('--color-brand-surface-muted-rgb', toRgbChannels(tokens.surfaceMuted));
  root.style.setProperty('--color-brand-border', tokens.border);
  root.style.setProperty('--color-brand-border-rgb', toRgbChannels(tokens.border));

  root.style.setProperty('--color-brand-text', tokens.text);
  root.style.setProperty('--color-brand-text-rgb', toRgbChannels(tokens.text));
  root.style.setProperty('--color-brand-text-soft', tokens.textSoft);
  root.style.setProperty('--color-brand-text-soft-rgb', toRgbChannels(tokens.textSoft));
  root.style.setProperty('--color-brand-on-primary', tokens.onPrimary);
  root.style.setProperty('--color-brand-on-primary-rgb', toRgbChannels(tokens.onPrimary));
  root.style.setProperty('--color-brand-on-secondary', tokens.onSecondary);
  root.style.setProperty('--color-brand-on-secondary-rgb', toRgbChannels(tokens.onSecondary));

  root.style.setProperty('--color-veraz-pink', tokens.primary);
  root.style.setProperty('--color-veraz-gold', tokens.secondary);
  root.style.setProperty('--color-veraz-cream', tokens.accent);
  root.style.setProperty('--color-veraz-brown', tokens.text);
  root.style.setProperty('--color-veraz-border', tokens.border);

  return tokens;
};
